import { Button } from '@heroui/button';
import { Card } from '@heroui/card';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { adminService } from '../../services/adminService';
import { WarmupStatus } from '../../types/admin';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const AdminWarmup: React.FC = () => {
  const [status, setStatus] = useState<WarmupStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    try {
      const s = await adminService.getWarmupStatus();
      setStatus(s);
    } catch (e: any) {
      toast.error('Failed to load warmup status');
    }
  };

  const runWarmup = async () => {
    try {
      setLoading(true);
      await adminService.runWarmup();
      toast.success('Warmup triggered');
      await loadStatus();
    } catch (e: any) {
      toast.error('Failed to trigger warmup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Admin Cache Warmup'
        description='Monitor and run cache warmup tasks for the store.'
        url={generateCanonicalUrl('/admin/warmup')}
        type='website'
      />
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>Cache Warmup</h1>
        <div className='space-x-2'>
          <Button color='primary' onPress={runWarmup} isLoading={loading}>
            Run Warmup
          </Button>
          <Button variant='flat' onPress={loadStatus}>
            Refresh
          </Button>
        </div>
      </div>

      <Card className='p-4'>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <div>
            <div className='text-sm text-gray-500'>Last Run (UTC)</div>
            <div className='font-semibold'>
              {status?.lastRunUtc
                ? new Date(status.lastRunUtc).toISOString()
                : '—'}
            </div>
          </div>
          <div>
            <div className='text-sm text-gray-500'>Running</div>
            <div className='font-semibold'>
              {status?.isRunning ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <div className='text-sm text-gray-500'>Success</div>
            <div className='font-semibold'>{status?.successCount ?? 0}</div>
          </div>
          <div>
            <div className='text-sm text-gray-500'>Failures</div>
            <div className='font-semibold text-red-600'>
              {status?.failureCount ?? 0}
            </div>
          </div>
        </div>
      </Card>

      <Card className='p-4'>
        <h2 className='text-lg font-semibold mb-3'>Tasks</h2>
        <div className='overflow-x-auto'>
          <table className='min-w-full text-sm'>
            <thead>
              <tr className='text-left border-b'>
                <th className='py-2 pr-4'>Task</th>
                <th className='py-2 pr-4'>Elapsed (ms)</th>
                <th className='py-2 pr-4'>Status</th>
                <th className='py-2'>Error</th>
              </tr>
            </thead>
            <tbody>
              {(status?.tasks || []).map((t, idx) => (
                <tr key={idx} className='border-b last:border-0'>
                  <td className='py-2 pr-4'>{t.name}</td>
                  <td className='py-2 pr-4'>{t.elapsedMs}</td>
                  <td className='py-2 pr-4'>
                    {t.success ? (
                      <span className='text-green-600'>Success</span>
                    ) : (
                      <span className='text-red-600'>Failed</span>
                    )}
                  </td>
                  <td className='py-2'>{t.error || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminWarmup;
